import { type FC, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

const pillStyles =
  "flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-sm";

interface NavPillProps {
  icon?: LucideIcon;
  label: string;
  onClick?: () => void;
  variant?: "static" | "interactive" | "highlight";
  isLoading?: boolean;
  children?: ReactNode;
  tooltip?: string;
  className?: string;
}

export const NavPill: FC<NavPillProps> = ({
  icon: Icon,
  label,
  onClick,
  variant = "interactive",
  isLoading,
  children,
  tooltip,
  className: classNameProp,
}) => {
  const variantStyles = {
    static: "border-transparent text-secondary",
    interactive: "text-primary hover:bg-subtle transition-colors",
    highlight: "border-transparent bg-green-100 text-brand font-medium",
  };

  const className = cn(pillStyles, variantStyles[variant], classNameProp);

  const wrapWithTooltip = (content: ReactNode) => {
    if (!tooltip) return content;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (variant === "static" || variant === "highlight") {
    return wrapWithTooltip(
      <div className={className}>
        {Icon && <Icon size={14} />}
        {children}
        {isLoading ? (
          <span className="h-3 w-16 animate-pulse rounded-full bg-subtle" />
        ) : (
          <span>{label}</span>
        )}
      </div>,
    );
  }

  return wrapWithTooltip(
    <button onClick={onClick} className={className}>
      {Icon && <Icon size={14} />}
      {children}
      <span>{label}</span>
    </button>,
  );
};
